import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import logoUrl from '@/assets/icons/logo.svg?url';
import chevronLeftUrl from '@/assets/icons/chevron-left.svg?url';
import gearFillUrl from '@/assets/icons/gear-fill.svg?url';
import gearEmptyUrl from '@/assets/icons/gear-empty.svg?url';
import homeUrl from '@/assets/icons/home.svg?url';
import homeEmptyUrl from '@/assets/icons/home-empty.svg?url';
import { ROUTES } from '@/constants/routes';

const Nav = styled.nav<{ $showBack?: boolean; $wide?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: ${({ $showBack }) => ($showBack ? '1fr max-content 1fr' : '1fr')};
  align-items: center;
  font-size: 17px;
  line-height: 22px;
  letter-spacing: -0.41px;
  font-weight: 600;
  justify-content: center;
  max-width: ${({ $wide }) => ($wide ? '760px' : '430px')};
  margin: 2px auto 0 auto;
  width: 100%;

  button {
    padding: 0;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #37b4eb;
  cursor: pointer;
  font-family: inherit;
  font-weight: 400;
  font-size: 17px;
  line-height: 24px;
  letter-spacing: -0.41px;
  display: flex;
  align-items: center;
  padding: 0;
`;

const BackIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 24px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
`;

const LogoIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const HomeButton = styled.button`
  background: none;
  border: none;
  font-family: inherit;
  font-size: 18px;
  cursor: pointer;
  user-select: none;
  justify-self: start;
`;

const GearButton = styled.button`
  background: none;
  border: none;
  font-family: inherit;
  font-size: 18px;
  cursor: pointer;
  user-select: none;
  justify-self: end;
`;

const GearIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HomeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface CleanHeaderProps {
  showBack?: boolean;
  showHome?: boolean;
  showGear?: boolean;
  showLogo?: boolean;
  backText?: string;
  centerText?: string;
  title?: string;
  wide?: boolean;
  onBack?: () => void;
  onGear?: () => void;
}

export function CleanHeader({
  showBack = false,
  showHome = false,
  showGear = false,
  showLogo = false,
  backText = 'Back',
  centerText = 'Shieldix',
  title,
  wide = false,
  onBack,
  onGear,
}: CleanHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const homeIconSrc = location.pathname.includes('/subscription') ? homeUrl : homeEmptyUrl;
  const gearIconSrc = location.pathname.includes('/policy') ? gearFillUrl : gearEmptyUrl;

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  const goHome = () => {
    navigate(ROUTES.ACCOUNT.SUBSCRIPTION);
  };

  const toGear = () => {
    if (onGear) {
      onGear();
    }
    else {
      navigate(ROUTES.POLICY.INDEX);
    }
  };

  return (
    <Nav $showBack={showBack || showHome} $wide={wide}>
      {showBack && (
        <BackButton onClick={handleBack} aria-label="Back">
          <BackIcon>
            <img src={chevronLeftUrl} alt="Back" />
          </BackIcon>
          <span>{backText}</span>
        </BackButton>
      )}
      {showHome && (
        <HomeButton onClick={goHome} aria-label="Home">
          <HomeIcon>
            <img src={homeIconSrc} alt="Home" />
          </HomeIcon>
        </HomeButton>
      )}
      <LogoContainer>
        {showLogo && (
          <Logo>
            <LogoIcon src={logoUrl} alt="Shieldix" />
          </Logo>
        )}
        <div>{title || centerText}</div>
      </LogoContainer>
      {showGear && (
        <GearButton onClick={toGear} aria-label="gear">
          <GearIcon>
            <img src={gearIconSrc} alt="Gear" />
          </GearIcon>
        </GearButton>
      )}
    </Nav>
  );
}
